import { useState, useEffect, useCallback } from 'react';
import { Clock, Loader2, ArrowRight, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '../lib/gapApi';

// Shared "Recent Analyses" list for a single analysis-type tab (General/Instagram/
// LinkedIn/etc). Fetches {id, company_name, website, created_at}[] from
// `${historyEndpoint}?limit=20` and hands the clicked id back via onSelect —
// the parent page owns fetching/rendering the full replay result.
// `deleteEndpoint` (base path for DELETE `${deleteEndpoint}/{id}`) defaults to
// `historyEndpoint` with a trailing "/history" stripped, matching the backend's
// /gap-analysis/{id} and /social/{platform}/{id} convention.
export default function RecentAnalysisPanel({ historyEndpoint, deleteEndpoint, activeId, onSelect, onDeleted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const deleteBase = deleteEndpoint || historyEndpoint.replace(/\/history$/, '');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${historyEndpoint}?limit=20`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch analysis history', err);
    } finally {
      setLoading(false);
    }
  }, [historyEndpoint]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved analysis? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await api.delete(`${deleteBase}/${id}`);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (onDeleted) onDeleted(id);
    } catch (err) {
      console.error('Failed to delete analysis', err);
      alert('Failed to delete analysis: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-6 mb-12">
        <Loader2 size={16} className="animate-spin" /> Loading recent analyses...
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 mb-12 animate-in fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-wider">
          <Clock size={16} className="text-slate-400" /> Recent Analyses
        </h3>
        <button onClick={fetchHistory} className="text-slate-500 hover:text-slate-300 transition-colors" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(item.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(item.id); } }}
            className={`relative text-left p-4 pr-10 rounded-xl border transition-all group cursor-pointer ${
              activeId === item.id
                ? 'bg-emerald-500/10 border-emerald-500/40'
                : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800'
            }`}
          >
            <button
              type="button"
              onClick={(e) => handleDelete(e, item.id)}
              disabled={deletingId === item.id}
              aria-label="Delete this analysis"
              title="Delete"
              className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-500 opacity-0 group-hover:opacity-100 hover:text-rose-400 hover:bg-rose-500/10 transition-all disabled:opacity-50"
            >
              {deletingId === item.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            </button>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-white text-sm truncate">{item.company_name}</span>
              <ArrowRight size={14} className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all shrink-0" />
            </div>
            <p className="text-xs text-slate-500 truncate mt-1">{item.website || 'No website'}</p>
            <p className="text-[11px] text-slate-600 mt-2">{new Date(item.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
